import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import AppInput from "../../components/Core/AppInput/AppInput";
import Button from "../../components/Core/Button/Button";
import Flex from "../../components/Layout/Flex";
import TableLayout from "../../components/Layout/TableLayout";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import EmptyState from "../../components/Shared/EmptyState/EmptyState";
import LoadingIndicator from "../../components/Shared/LoadingIndicator/LoadingIndicator";
import Pagination from "../../components/Shared/Pagination/Pagination";
import SortIcon from "../../components/Shared/SortIcon/SortIcon";
import Tab from "../../components/Shared/Tabs/Tab";
import { ContractBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { PAGE_SIZE } from "../../configs/constants/Pagination";
import { routes } from "../../configs/constants/routes";
import useDebounce from "../../hooks/useDebounce";
import Contract from "../../models/Contract";
import { CONTRACT_FILTER, FilterList } from "../../models/Filters";
import { CONTRACT_SORTING_COLUMN, SORT_ORDER } from "../../models/Sorting";
import { useContracts } from "../../networking/contract.hooks";
import { formatRoute } from "../../utils/route.utils";

type ContractFilters = { active?: boolean; isDeleted?: boolean };

const ContractsListPage: React.FC = () => {
	const navigate = useNavigate();
	const { t } = useTranslation();

	const [pageNumber, setPageNumber] = useState<number>(1);
	const [searchTerm, setSearchTerm] = useState<string>("");
	const keyword = useDebounce(searchTerm, 500);
	const [order, setOrder] = useState<SORT_ORDER>();
	const [sort, setSort] = useState<CONTRACT_SORTING_COLUMN>();
	const [filter, setFilter] = useState<CONTRACT_FILTER>("active-not-archived");
	const [filterQuery, setFilterQuery] = useState<ContractFilters>({
		active: true,
		isDeleted: false,
	});

	const {
		contracts = [],
		totalCount: totalContractCount = 0,
		isLoading,
	} = useContracts({
		page: pageNumber,
		pageSize: PAGE_SIZE,
		keyword,
		sort,
		order: order as unknown as 1 | -1,
		active: filterQuery?.active,
		isDeleted: filterQuery?.isDeleted,
	});

	const navigateToContractDetails = (contractId: string) => {
		const routePath =
			formatRoute(routes.CONTRACT_DETAILS, [contractId]) + "/" + routes.CONTRACT_INFO;
		navigate(routePath);
	};

	const handleSortingOrder = () => {
		if (order === SORT_ORDER.ASC) setOrder(SORT_ORDER.DESC);
		else setOrder(SORT_ORDER.ASC);
	};

	const handleSortColumn = (sort?: CONTRACT_SORTING_COLUMN) => {
		handleSortingOrder();
		setSort(sort);
	};

	const contractFilterList: FilterList<CONTRACT_FILTER>[] = [
		{
			name: "ACTIVE_BUT_NOT_ARCHIVED",
			filter: "active-not-archived",
		},
		{
			name: "INACTIVE_BUT_NOT_ARCHIVED",
			filter: "inactive-not-archived",
		},
		{
			name: "ARCHIVED",
			filter: "archived",
		},
	];

	const handleFilterOnChange = (filter: CONTRACT_FILTER) => {
		setFilter(filter);

		switch (filter) {
			// case "all":
			// 	setFilterQuery({});
			// 	break;
			case "active-not-archived":
				setFilterQuery({ active: true, isDeleted: false });
				break;
			case "inactive-not-archived":
				setFilterQuery({ active: false, isDeleted: false });
				break;
			case "archived":
				setFilterQuery({ isDeleted: true });
				break;
		}
	};

	function getStatusDisplayName(contract: Pick<Contract, "isDeleted" | "active">) {
		if (contract.isDeleted) {
			return t("contract.archived");
		}
		return t(contract.active ? "ACTIVE" : "INACTIVE");
	}

	return (
		<>
			<Breadcrumbs items={ContractBreadcrumbs} />
			<div className="p-4 px-6">
				<Flex justifyContent="space-between">
					<AppInput
						containerWidth="200px"
						placeholder={t("SEARCH")}
						icon="search"
						onChange={(e: any) => setSearchTerm(e.target.value)}
					/>
					<Link to={routes.CONTRACTS_CREATE}>
						<Button width="9.75rem">{t("CREATE_CONTRACT")}</Button>
					</Link>
				</Flex>

				<Flex margin="0 0 0.75rem 0" gap="0.6rem">
					{contractFilterList.map((filterTab, index) => (
						<Tab
							action={() => handleFilterOnChange(filterTab.filter)}
							key={index}
							active={filterTab.filter === filter}
							text={filterTab.name}
							role="filter"
						/>
					))}
				</Flex>

				{isLoading ? (
					<LoadingIndicator />
				) : (
					<div>
						{contracts.length > 0 ? (
							<TableLayout>
								<table>
									<thead>
										<tr>
											<th onClick={() => handleSortColumn(CONTRACT_SORTING_COLUMN.NAME)}>
												{t("NAME")}

												<SortIcon
													sortType={
														sort === CONTRACT_SORTING_COLUMN.NAME ? order : SORT_ORDER.DEFAULT
													}
												/>
											</th>
											<th onClick={() => handleSortColumn(CONTRACT_SORTING_COLUMN.CREATED)}>
												{t("CREATED")}

												<SortIcon
													sortType={
														sort === CONTRACT_SORTING_COLUMN.CREATED ? order : SORT_ORDER.DEFAULT
													}
												/>
											</th>
											<th onClick={() => handleSortColumn(CONTRACT_SORTING_COLUMN.UPDATED)}>
												{t("UPDATED")}

												<SortIcon
													sortType={
														sort === CONTRACT_SORTING_COLUMN.UPDATED ? order : SORT_ORDER.DEFAULT
													}
												/>
											</th>
											<th onClick={() => handleSortColumn(CONTRACT_SORTING_COLUMN.STATUS)}>
												{t("STATUS")}

												<SortIcon
													sortType={
														sort === CONTRACT_SORTING_COLUMN.STATUS ? order : SORT_ORDER.DEFAULT
													}
												/>
											</th>
										</tr>
									</thead>
									<tbody>
										{contracts.map(({ id, name, metadata, active, isDeleted }) => (
											<tr
												key={id}
												onClick={() => navigateToContractDetails(id)}
												className={isDeleted || !active ? "warning" : ""}
											>
												<TableData>{name}</TableData>
												<td>{moment(metadata?.created).format(DATE_TIME_FORMAT_TYPES.DATE)}</td>
												<td>{moment(metadata?.updated).format(DATE_TIME_FORMAT_TYPES.DATE)}</td>
												<td style={{ color: isDeleted ? "red" : undefined }}>
													{getStatusDisplayName({ active, isDeleted })}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</TableLayout>
						) : (
							<EmptyState margin="2rem 0" label="NO_CONTRACTS" />
						)}
						{totalContractCount > PAGE_SIZE && (
							<Pagination
								currentPage={pageNumber}
								totalCount={totalContractCount}
								onClickItem={(e) => setPageNumber(e)}
							/>
						)}
					</div>
				)}
			</div>
		</>
	);
};

const TableData = styled.td`
	width: 25rem;
`;

export default ContractsListPage;
